import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Reel edit-model (EDL) + audio stems ─────────────────────

/** Load the saved reel edit-model (EDL), or { edl: null }. */
export async function getReelEdit(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/edit`);
  return data;
}

/** Save the reel edit-model (EDL). */
export async function putReelEdit(projectId, edl) {
  const { data } = await axios.put(`${API}/storyboard/${projectId}/edit`, { edl });
  return data;
}

/** Upload an audio stem (music / SFX). Returns { key, url, filename }. */
export async function uploadReelAsset(projectId, file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${API}/storyboard/${projectId}/reel/asset`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Generate an instrumental music stem (Lyria) from a text prompt.
 *  Returns { key, url, filename, seconds, mime }. */
export async function generateReelMusic(projectId, prompt, quality = 'clip') {
  const { data } = await axios.post(`${API}/storyboard/${projectId}/music`, { prompt, quality }, {
    timeout: 200000,
  });
  return data;
}

// ─── MP4 export (server-side FFmpeg render of the EDL) ───────────

/** Start a background MP4 render of the current edit-model. Returns { job_id, status }. */
export async function startReelRender(projectId, edl) {
  const { data } = await axios.post(`${API}/storyboard/${projectId}/render`, edl ? { edl } : {});
  return data;
}

/** Poll a render job. Returns { status, error, bytes, seconds, download_url? }. */
export async function getReelRenderStatus(jobId) {
  const { data } = await axios.get(`${API}/storyboard/render/${jobId}/status`);
  return data;
}

/** Absolute URL to download a finished render (public-by-job-id). */
export function getReelRenderDownloadUrl(jobId) {
  return `${API}/storyboard/render/${jobId}/download`;
}

/** List this project's MP4 exports (newest first). */
export async function listReelRenders(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/renders`);
  return data.renders || [];
}

/** Delete an export (GridFS file + record). */
export async function deleteReelRender(jobId) {
  const { data } = await axios.delete(`${API}/storyboard/render/${jobId}`);
  return data;
}

// ─── Visual assets dropped on the timeline (images/videos) ───────

/** Absolute URL for a dropped visual asset (image/video), public-by-key. */
export function getReelAssetUrl(key) {
  return `${API}/storyboard/reel-asset/${key}`;
}

// ─── AI VFX (transform a plate clip with a prompt) ───────────────

/** Media bin: every visual/audio asset already uploaded to the project. */
export async function listReelAssets(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/reel/assets`);
  return data; // { visual: [...], audio: [...] }
}

/** The VFX model picker (label, tier, $/sec, clip limits, availability). */
export async function getVfxProviders() {
  const { data } = await axios.get(`${API}/storyboard/vfx/providers`);
  return data; // { providers: [...], default }
}

/** Start an AI VFX transform on a plate clip. opts = { plate_url, prompt, mode, provider?, ref_url?, resolution?, num_frames? }.
 *  Returns { job_id, status }. */
export async function startVfx(projectId, opts) {
  const { data } = await axios.post(`${API}/storyboard/${projectId}/vfx`, opts);
  return data;
}

/** Recent VFX jobs for a project (to re-link/resume renders after reopening the editor). */
export async function getVfxJobs(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/vfx/jobs`);
  return data; // { jobs: [...] }
}

/** Poll a VFX job. Returns { status, error, mode, asset_key?, url? }. */
export async function getVfxStatus(jobId) {
  const { data } = await axios.get(`${API}/storyboard/vfx/${jobId}/status`);
  return data;
}

// ─── Veo previs: animate a storyboard frame into a moving clip ──

/** Start animating a frame (image-to-video). Returns { status, frame_id }. */
export async function animateShot(projectId, frameId, prompt) {
  const { data } = await axios.post(`${API}/storyboard/${projectId}/animate/${frameId}`, prompt ? { prompt } : {});
  return data;
}

/** Per-frame animation status: [{ frame_id, status, asset_key, error }]. */
export async function getAnimateStatus(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/animate/status`);
  return data.animations || [];
}

/** Upload an image/video to drop on the timeline. meta = { width, height, duration }.
 *  Returns { key, url, kind, mime, width, height, duration }. */
export async function uploadReelVisual(projectId, file, meta = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('width', String(Math.round(meta.width || 0)));
  form.append('height', String(Math.round(meta.height || 0)));
  form.append('duration', String(meta.duration || 0));
  const { data } = await axios.post(`${API}/storyboard/${projectId}/reel/visual`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
  });
  return data;
}
